import { useTranslation } from 'react-i18next';
import { Navigate, createBrowserRouter } from 'react-router';

import { useWorkspace } from '@/api/workspaces.ts';
import RequireSession from '@/auth/RequireSession.tsx';
import { ClientCrumb, ProjectCrumb } from '@/clients/components/ClientCrumbs.tsx';
import ConnectionCrumb, { OwnConnectionsCrumb } from '@/connections/components/ConnectionCrumb.tsx';
import {
    EmployeeClientCrumb,
    EmployeeProjectCrumb,
    EmployeeRecapCrumb,
    EmployeeRecapProjectCrumbs,
} from '@/employee/components/EmployeeCrumbs.tsx';
import { LEGAL_DOCUMENTS } from '@/legal/documents.ts';
import RecapCrumb, { RecapParentCrumbs } from '@/recaps/components/RecapCrumb.tsx';
import { publicIdOf } from '@/recaps/recapPaths.ts';
import SettingsTabCrumb from '@/settings/components/SettingsTabCrumb.tsx';
import SourceCrumb, { SourceParentCrumbs } from '@/sources/components/SourceCrumb.tsx';
import AppearanceCrumb from '@/user-settings/components/AppearanceCrumb.tsx';
import NotificationsCrumb from '@/user-settings/components/NotificationsCrumb.tsx';

import AppLayout, { type RouteHandle } from './AppLayout.tsx';
import ErrorPage from './ErrorPage.tsx';
import RootLayout from './RootLayout.tsx';
import type { NavKey } from './navigation.ts';

const NavCrumb = ({ navKey }: { navKey: NavKey }) => {
    const { t } = useTranslation();
    return <>{t(`nav.${navKey}`)}</>;
};

const TeamCrumb = () => {
    const { t } = useTranslation();
    return <>{t('clients.tabs.team')}</>;
};

const PeopleCrumb = () => {
    const { t } = useTranslation();
    return <>{t('clients.tabs.people')}</>;
};

const ClientTabCrumb = () => {
    const { t } = useTranslation();
    return <>{t('clients.tabs.client')}</>;
};

const EditCrumb = () => {
    const { t } = useTranslation();
    return <>{t('common.edit')}</>;
};

const DraftCrumb = () => {
    const { t } = useTranslation();
    return <>{t('recaps.status.draft')}</>;
};

type PageModule = () => Promise<{ default: React.ComponentType }>;

// Pages are loaded when they're first opened, so the app starts with only the frame and the page it opens on.
const page = (load: PageModule) => async () => ({
    Component: (await load()).default,
});

// A page members and a client's employees open at the same URL, each their own, by the workspace's role
// (plan/DECISIONS.md D61). Every other page in a workspace is the members', whose endpoints answer 404 to an employee.
// Loaded once, so the routes of a page's tabs that share it render the same component, which stays mounted from tab to
// tab (the tabs' line slides over rather than jumping).
const pageByRole = (loadMemberPage: PageModule, loadEmployeePage: PageModule) => {
    let loaded: Promise<{ Component: React.ComponentType }> | undefined;
    return () =>
        (loaded ??= Promise.all([loadMemberPage(), loadEmployeePage()]).then(
            ([{ default: MemberPage }, { default: EmployeePage }]) => ({
                Component: () => (useWorkspace().role === 'employee' ? <EmployeePage /> : <MemberPage />),
            })
        ));
};

// A client's tabs and a project's.
const clientPage = pageByRole(
    () => import('@/clients/ClientPage.tsx'),
    () => import('@/employee/EmployeeClientRedirect.tsx')
);
const projectPage = pageByRole(
    () => import('@/clients/ProjectPage.tsx'),
    () => import('@/employee/EmployeeProjectPage.tsx')
);
// A client's employee has their client's projects on their own Übersicht.
const projectsPage = pageByRole(
    () => import('@/clients/ProjectsPage.tsx'),
    () => import('@/employee/EmployeeClientRedirect.tsx')
);

const navHandle = (navKey: NavKey): RouteHandle => ({ crumb: () => <NavCrumb navKey={navKey} /> });

// A section both members and a client's employees have.
const sharedNavHandle = (navKey: NavKey): RouteHandle => ({
    crumb: () => <NavCrumb navKey={navKey} />,
    employeeCrumb: () => <NavCrumb navKey={navKey} />,
});

// Every page belongs to a workspace (/w/:workspaceSlug/…); '/' continues in the one used last, or is the start page for
// whoever isn't signed in. Outside them are only signing in and up, a sign-in code's link, the setup, an MCP client's
// consent screen, invitation links and a project's public link.
const router = createBrowserRouter([
    {
        // react-aria's links and buttons with an href navigate through the router on every page.
        element: <RootLayout />,
        children: [
            { path: '/', lazy: page(() => import('@/landing/HomeRoute.tsx')), errorElement: <ErrorPage /> },
            { path: '/sign-in', lazy: page(() => import('@/auth/SignInPage.tsx')), errorElement: <ErrorPage /> },
            { path: '/sign-up', lazy: page(() => import('@/auth/SignUpPage.tsx')), errorElement: <ErrorPage /> },
            // The link in a sign-in code's email (plan task 78).
            {
                path: '/sign-in/link/:token',
                lazy: page(() => import('@/auth/SignInLinkPage.tsx')),
                errorElement: <ErrorPage />,
            },
            {
                path: '/invitations/:token',
                lazy: page(() => import('@/auth/InvitationPage.tsx')),
                errorElement: <ErrorPage />,
            },
            {
                path: '/employee-invitations/:token',
                lazy: page(() => import('@/auth/EmployeeInvitationPage.tsx')),
                errorElement: <ErrorPage />,
            },
            // A project's public link, which anyone opens without an account (plan/DECISIONS.md D103).
            {
                path: '/p/:token',
                lazy: page(() => import('@/public/PublicLayout.tsx')),
                errorElement: <ErrorPage />,
                children: [
                    { index: true, lazy: page(() => import('@/public/PublicProjectPage.tsx')) },
                    { path: 'recaps/:recapRef', lazy: page(() => import('@/public/PublicRecapPage.tsx')) },
                ],
            },
            // A recap's own public link (plan task 54).
            {
                path: '/r/:token',
                lazy: page(() => import('@/public/SharedRecapPage.tsx')),
                errorElement: <ErrorPage />,
            },
            // The legal pages (plan task 57), public like the start page that links them.
            ...LEGAL_DOCUMENTS.map((document) => ({
                path: `/${document}`,
                lazy: async () => {
                    const { default: LegalPage } = await import('@/legal/LegalPage.tsx');
                    return { element: <LegalPage legalDocument={document} /> };
                },
                errorElement: <ErrorPage />,
            })),
            // The changelog (plan task 99), public like the legal pages.
            {
                path: '/changelog',
                lazy: page(() => import('@/changelog/ChangelogPage.tsx')),
                errorElement: <ErrorPage />,
            },
            {
                element: <RequireSession />,
                errorElement: <ErrorPage />,
                children: [
                    { path: '/setup', lazy: page(() => import('@/auth/SetupPage.tsx')), errorElement: <ErrorPage /> },
                    // An MCP client's consent screen, its OAuth authorization endpoint (plan task 70).
                    {
                        path: '/oauth/authorize',
                        lazy: page(() => import('@/auth/OAuthAuthorizePage.tsx')),
                        errorElement: <ErrorPage />,
                    },
                    {
                        id: 'app',
                        path: '/w/:workspaceSlug',
                        element: <AppLayout />,
                        errorElement: <ErrorPage />,
                        // Shown on the first load while the opened page's code arrives; AppLayout itself also waits for the workspaces.
                        HydrateFallback: () => null,
                        children: [
                            {
                                // Nested so page errors render inside the app frame instead of replacing it.
                                errorElement: <ErrorPage isInAppFrame />,
                                children: [
                                    {
                                        index: true,
                                        lazy: pageByRole(
                                            () => import('@/home/HomePage.tsx'),
                                            () => import('@/employee/EmployeeHomePage.tsx')
                                        ),
                                        handle: sharedNavHandle('home'),
                                    },
                                    {
                                        path: 'activities',
                                        lazy: page(() => import('@/activities/ActivitiesPage.tsx')),
                                        handle: navHandle('activities'),
                                    },
                                    {
                                        // Every project, with its client or the team's own (plan/DECISIONS.md D185).
                                        path: 'projects',
                                        handle: sharedNavHandle('projects'),
                                        children: [
                                            { index: true, lazy: projectsPage },
                                            {
                                                path: ':projectSlug',
                                                handle: {
                                                    crumb: () => <ProjectCrumb />,
                                                    employeeCrumb: () => <EmployeeProjectCrumb />,
                                                } satisfies RouteHandle,
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: projectPage,
                                                    },
                                                    // The project page's other tabs.
                                                    {
                                                        path: 'activities',
                                                        lazy: projectPage,
                                                        handle: navHandle('activities'),
                                                    },
                                                    {
                                                        path: 'team',
                                                        lazy: projectPage,
                                                        handle: { crumb: () => <TeamCrumb /> } satisfies RouteHandle,
                                                    },
                                                    {
                                                        path: 'client',
                                                        lazy: projectPage,
                                                        handle: {
                                                            crumb: () => <ClientTabCrumb />,
                                                        } satisfies RouteHandle,
                                                    },
                                                    {
                                                        path: 'settings',
                                                        lazy: projectPage,
                                                        handle: navHandle('settings'),
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: 'clients',
                                        handle: navHandle('clients'),
                                        children: [
                                            { index: true, lazy: page(() => import('@/clients/ClientsPage.tsx')) },
                                            {
                                                path: ':clientSlug',
                                                handle: {
                                                    crumb: () => <ClientCrumb />,
                                                    employeeCrumb: () => <EmployeeClientCrumb />,
                                                } satisfies RouteHandle,
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: clientPage,
                                                    },
                                                    // The client page's tabs, each a path of its own.
                                                    {
                                                        path: 'activities',
                                                        lazy: clientPage,
                                                        handle: navHandle('activities'),
                                                    },
                                                    {
                                                        path: 'team',
                                                        lazy: clientPage,
                                                        handle: { crumb: () => <TeamCrumb /> } satisfies RouteHandle,
                                                    },
                                                    {
                                                        path: 'people',
                                                        lazy: clientPage,
                                                        handle: { crumb: () => <PeopleCrumb /> } satisfies RouteHandle,
                                                    },
                                                    {
                                                        path: 'settings',
                                                        lazy: clientPage,
                                                        handle: navHandle('settings'),
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: 'recaps',
                                        lazy: pageByRole(
                                            () => import('@/recaps/RecapsPage.tsx'),
                                            () => import('@/employee/EmployeeRecapsPage.tsx')
                                        ),
                                        handle: sharedNavHandle('recaps'),
                                    },
                                    {
                                        path: 'recaps/:recapRef',
                                        handle: {
                                            crumb: (params) => (
                                                <RecapCrumb publicId={publicIdOf(params.recapRef ?? '')} />
                                            ),
                                            parentCrumbs: (params) => (
                                                <RecapParentCrumbs publicId={publicIdOf(params.recapRef ?? '')} />
                                            ),
                                            employeeCrumb: (params) => (
                                                <EmployeeRecapCrumb publicId={publicIdOf(params.recapRef ?? '')} />
                                            ),
                                            employeeParentCrumbs: (params) => (
                                                <EmployeeRecapProjectCrumbs
                                                    publicId={publicIdOf(params.recapRef ?? '')}
                                                />
                                            ),
                                        } satisfies RouteHandle,
                                        // One page: the published version at the recap's URL, its draft previewed
                                        // at /draft and edited at /edit (plan/DECISIONS.md D107).
                                        children: [
                                            {
                                                index: true,
                                                lazy: pageByRole(
                                                    () => import('@/recaps/RecapPage.tsx'),
                                                    () => import('@/employee/EmployeeRecapPage.tsx')
                                                ),
                                            },
                                            {
                                                path: 'draft',
                                                lazy: page(() => import('@/recaps/RecapPage.tsx')),
                                                handle: { crumb: () => <DraftCrumb /> } satisfies RouteHandle,
                                            },
                                            {
                                                path: 'edit',
                                                lazy: page(() => import('@/recaps/RecapPage.tsx')),
                                                handle: { crumb: () => <EditCrumb /> } satisfies RouteHandle,
                                            },
                                        ],
                                    },
                                    {
                                        path: 'sources',
                                        lazy: page(() => import('@/sources/SourcesPage.tsx')),
                                        handle: navHandle('sources'),
                                    },
                                    {
                                        // Its crumbs read Kunden › client › Projekte › project › source with a client,
                                        // Quellen › source without (plan/DECISIONS.md D129).
                                        path: 'sources/:sourceId',
                                        lazy: page(() => import('@/sources/SourcePage.tsx')),
                                        handle: {
                                            crumb: (params) => <SourceCrumb sourceId={params.sourceId ?? ''} />,
                                            parentCrumbs: (params) => (
                                                <SourceParentCrumbs sourceId={params.sourceId ?? ''} />
                                            ),
                                        } satisfies RouteHandle,
                                    },
                                    {
                                        // Nested, so the crumbs read Einstellungen › Verbindungen › connection.
                                        path: 'settings',
                                        handle: navHandle('settings'),
                                        // Every tab one level down, as the connections' has to be for a connection's page,
                                        // so the page stays mounted from tab to tab (the tabs' line slides over rather than
                                        // jumping).
                                        children: [
                                            {
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: page(() => import('@/settings/SettingsPage.tsx')),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'connections',
                                                handle: {
                                                    crumb: () => <SettingsTabCrumb tab="connections" />,
                                                } satisfies RouteHandle,
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: page(() => import('@/settings/SettingsPage.tsx')),
                                                    },
                                                    {
                                                        path: ':connectionId',
                                                        lazy: page(() => import('@/connections/ConnectionPage.tsx')),
                                                        handle: {
                                                            crumb: () => <ConnectionCrumb />,
                                                        } satisfies RouteHandle,
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'team',
                                                handle: {
                                                    crumb: () => <SettingsTabCrumb tab="team" />,
                                                } satisfies RouteHandle,
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: page(() => import('@/settings/SettingsPage.tsx')),
                                                    },
                                                ],
                                            },
                                            // Its path before the tab was called "Team" (plan/DECISIONS.md D181).
                                            {
                                                path: 'members',
                                                element: <Navigate to="../team" relative="path" replace />,
                                            },
                                        ],
                                    },
                                    // Not about the workspace, but shown in its frame so the rail and switcher stay in place.
                                    {
                                        // Design options tried out, kept to look at again (web/AGENTS.md).
                                        path: 'experiments/recap-items',
                                        lazy: page(() => import('@/experiments/RecapItemsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/recap-decisions',
                                        lazy: page(() => import('@/experiments/RecapDecisionsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/recap-topics',
                                        lazy: page(() => import('@/experiments/RecapTopicsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/version-card',
                                        lazy: page(() => import('@/experiments/VersionCardExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/api-connection-page',
                                        lazy: page(() => import('@/experiments/ApiConnectionPageExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/api-key-dialog',
                                        lazy: page(() => import('@/experiments/ApiKeyDialogExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/forward-wait',
                                        lazy: page(() => import('@/experiments/ForwardWaitExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/connections',
                                        lazy: page(() => import('@/experiments/ConnectionsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/connection-page',
                                        lazy: page(() => import('@/experiments/ConnectionPageExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/feed-dialog',
                                        lazy: page(() => import('@/experiments/FeedDialogExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/rule-query',
                                        lazy: page(() => import('@/experiments/RuleQueryExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/rule-conditions',
                                        lazy: page(() => import('@/experiments/RuleConditionsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/draft-notice',
                                        lazy: page(() => import('@/experiments/DraftNoticeExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/attention',
                                        lazy: page(() => import('@/experiments/AttentionExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/filter-bar',
                                        lazy: page(() => import('@/experiments/FilterBarExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/mcp-connection',
                                        lazy: page(() => import('@/experiments/McpConnectionExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/stars',
                                        lazy: page(() => import('@/experiments/StarsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/workspace-settings',
                                        lazy: page(() => import('@/experiments/WorkspaceSettingsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/tabs',
                                        lazy: page(() => import('@/experiments/TabsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/sign-in',
                                        lazy: page(() => import('@/experiments/SignInExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/onboarding',
                                        lazy: page(() => import('@/experiments/OnboardingExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/empty-pages',
                                        lazy: page(() => import('@/experiments/EmptyPagesExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/start-page',
                                        lazy: page(() => import('@/experiments/StartPageExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/legal-pages',
                                        lazy: page(() => import('@/experiments/LegalPagesExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/empty-sections',
                                        lazy: page(() => import('@/experiments/EmptySectionsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/small-empty-sections',
                                        lazy: page(() => import('@/experiments/SmallEmptySectionsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/list-more',
                                        lazy: page(() => import('@/experiments/ListMoreExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/section-actions',
                                        lazy: page(() => import('@/experiments/SectionActionsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/scope-select',
                                        lazy: page(() => import('@/experiments/ScopeSelectExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/danger-zone',
                                        lazy: page(() => import('@/experiments/DangerZoneExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/provider-picker',
                                        lazy: page(() => import('@/experiments/ProviderPickerExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/home-projects',
                                        lazy: page(() => import('@/experiments/HomeProjectsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/process-again',
                                        lazy: page(() => import('@/experiments/ProcessAgainExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/recap-row',
                                        lazy: page(() => import('@/experiments/RecapRowExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/header-media',
                                        lazy: page(() => import('@/experiments/HeaderMediaExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/project-client',
                                        lazy: page(() => import('@/experiments/ProjectClientExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/mine-filter',
                                        lazy: page(() => import('@/experiments/MineFilterExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/client-placeholder',
                                        lazy: page(() => import('@/experiments/ClientPlaceholderExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/client-projects',
                                        lazy: page(() => import('@/experiments/ClientProjectsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/nav-icons',
                                        lazy: page(() => import('@/experiments/NavIconsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/user-menu',
                                        lazy: page(() => import('@/experiments/UserMenuExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/people',
                                        lazy: page(() => import('@/experiments/PeopleExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/source-files',
                                        lazy: page(() => import('@/experiments/SourceFilesExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/you-tag',
                                        lazy: page(() => import('@/experiments/YouTagExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/connection-picker',
                                        lazy: page(() => import('@/experiments/ConnectionPickerExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/auto-save',
                                        lazy: page(() => import('@/experiments/AutoSaveExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/no-results',
                                        lazy: page(() => import('@/experiments/NoResultsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/colors',
                                        lazy: page(() => import('@/experiments/ColorsExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/error-page',
                                        lazy: page(() => import('@/experiments/ErrorPageExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/logo',
                                        lazy: page(() => import('@/experiments/LogoExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/mail-onboarding',
                                        lazy: page(() => import('@/experiments/MailOnboardingExperiment.tsx')),
                                    },
                                    {
                                        path: 'experiments/how-it-works',
                                        lazy: page(() => import('@/experiments/HowItWorksExperiment.tsx')),
                                    },
                                    {
                                        // Every core component in the variants the pages use (plan task 87).
                                        path: 'components',
                                        lazy: page(() => import('@/components-overview/ComponentsOverviewPage.tsx')),
                                    },
                                    {
                                        // Nested, so the crumbs read Kontoeinstellungen › Meine Verbindungen ›
                                        // connection, for members and a client's employees alike (plan task 35).
                                        path: 'user-settings',
                                        handle: sharedNavHandle('userSettings'),
                                        // Every tab one level down, as in the workspace's settings.
                                        children: [
                                            {
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: page(
                                                            () => import('@/user-settings/UserSettingsPage.tsx')
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'appearance',
                                                handle: {
                                                    crumb: () => <AppearanceCrumb />,
                                                    employeeCrumb: () => <AppearanceCrumb />,
                                                } satisfies RouteHandle,
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: page(
                                                            () => import('@/user-settings/UserSettingsPage.tsx')
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'notifications',
                                                handle: {
                                                    crumb: () => <NotificationsCrumb />,
                                                    employeeCrumb: () => <NotificationsCrumb />,
                                                } satisfies RouteHandle,
                                                children: [
                                                    {
                                                        index: true,
                                                        lazy: page(
                                                            () => import('@/user-settings/UserSettingsPage.tsx')
                                                        ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'connections',
                                                handle: {
                                                    crumb: () => <OwnConnectionsCrumb />,
                                                    employeeCrumb: () => <OwnConnectionsCrumb />,
                                                } satisfies RouteHandle,
                                                children: [
                                                    // The settings' tab of the user's own connections.
                                                    {
                                                        index: true,
                                                        lazy: page(
                                                            () => import('@/user-settings/UserSettingsPage.tsx')
                                                        ),
                                                    },
                                                    {
                                                        path: ':connectionId',
                                                        lazy: page(() => import('@/connections/ConnectionPage.tsx')),
                                                        handle: {
                                                            crumb: () => <ConnectionCrumb />,
                                                            employeeCrumb: () => <ConnectionCrumb />,
                                                        } satisfies RouteHandle,
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    { path: '*', element: <ErrorPage isInAppFrame /> },
                                ],
                            },
                        ],
                    },
                ],
            },
            { path: '*', element: <ErrorPage /> },
        ],
    },
]);

export default router;
